/**
 * Транспорт клиентского API.
 *
 * Три обязанности и ничего больше:
 *   1. Подписать запрос заголовком `Authorization: tma <initData>` (ТЗ §5, §16). Своих
 *      сессий, кук и токенов у приложения нет: единственное доказательство личности —
 *      подпись Telegram, и она проверяется на КАЖДОМ защищённом запросе.
 *   2. Превратить любой неуспех в `ApiFailure` с ключом i18n. Текста ошибок здесь нет
 *      (AGENTS.md, правило 2) — только ключ и подстановки.
 *   3. Не потерять отказ. Ни один путь не возвращает `null` «на всякий случай»: молчаливый
 *      пустой результат неотличим от «сервер ответил пустотой», и экран показал бы
 *      пустой каталог вместо сообщения о недоступном бэкенде (AGENTS.md, правило 7).
 */

/** Схема авторизации `@telegram-apps`. Значение фиксировано протоколом, не настройка. */
export const AUTH_SCHEME = 'tma';

/** Отказ API: ключ i18n, HTTP-статус и подстановки. Формой совпадает с `ApiError.toJSON()`. */
export class ApiFailure extends Error {
  readonly key: string;
  readonly status: number;
  readonly details: Record<string, string | number> | undefined;

  constructor(key: string, status: number, details?: Record<string, string | number>) {
    // message нужен логам и стек-трейсам; на экран уходит переведённый ключ.
    super(`${key} (HTTP ${status})`);
    this.name = 'ApiFailure';
    this.key = key;
    this.status = status;
    this.details = details;
  }
}

export function isApiFailure(error: unknown): error is ApiFailure {
  return error instanceof ApiFailure;
}

/**
 * Заголовок авторизации.
 *
 * `initData` уходит БАЙТ В БАЙТ. Ни `trim()`, ни `encodeURIComponent()`, ни какая-либо
 * нормализация недопустимы: подпись HMAC считается ровно по той строке, что выдал Telegram,
 * и любое «причёсывание» ломает проверку на бэкенде, давая 401 без внятной причины.
 *
 * Пустая строка — отказ на месте, а не запрос без заголовка: сервер ответил бы тем же 401,
 * но пройдя лишний round-trip и записав в лог чужую по смыслу ошибку.
 */
export function authHeader(rawInitData: string | null | undefined): { Authorization: string } {
  if (typeof rawInitData !== 'string' || rawInitData.trim().length === 0) {
    throw new ApiFailure('err.noInitData', 401);
  }
  return { Authorization: `${AUTH_SCHEME} ${rawInitData}` };
}

/**
 * Разбор тела отказа.
 *
 * Ожидается `{ error: 'err.…', details?: {…} }`. Всё остальное — не «неизвестная ошибка»,
 * а `err.unparseable` со статусом в подстановках: по такому сообщению видно, что упал
 * не домен, а транспорт (прокси, HTML-страница ошибки, обрыв).
 */
export function parseErrorBody(status: number, body: unknown): ApiFailure {
  if (typeof body === 'object' && body !== null) {
    const record = body as Record<string, unknown>;
    const key = record['error'];
    if (typeof key === 'string' && key.length > 0) {
      const details = record['details'];
      return new ApiFailure(key, status, isDetails(details) ? details : undefined);
    }
  }
  return new ApiFailure('err.unparseable', status, { status });
}

function isDetails(value: unknown): value is Record<string, string | number> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every(
    (item) => typeof item === 'string' || typeof item === 'number',
  );
}

export type QueryParams = Record<string, string | number | undefined>;

/** Сборка query: `undefined` не попадает в строку вовсе, а не превращается в `?x=undefined`. */
export function buildQuery(params: QueryParams | undefined): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [name, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(name, String(value));
  }
  const encoded = search.toString();
  return encoded.length > 0 ? `?${encoded}` : '';
}

export interface HttpTransportOptions {
  /** База API. В dev это `/api` через прокси Vite, в проде — тот же путь за общим Nginx. */
  readonly baseUrl: string;
  /** Актуальная `initData`. Функция, а не строка: SDK перевыпускает её на длинной сессии. */
  readonly getInitData: () => string | null | undefined;
  /** Подмена `fetch` для тестов. */
  readonly fetchImpl?: typeof fetch;
}

export interface RequestOptions {
  readonly method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly path: string;
  readonly query?: QueryParams;
  readonly body?: unknown;
  /** Нужна ли подпись. Каталог, страница организации и слоты открыты без неё. */
  readonly auth: boolean;
  /**
   * Ответ без тела — законный исход, а не сломанный контракт.
   *
   * Ставится ТОЛЬКО там, где бэкенд отвечает 204: удаление отпуска и четыре кнопки
   * статусов в календаре. Без этого признака пустое тело считается дефектом контракта
   * (см. `err.unparseable` ниже), и это правильное поведение по умолчанию: молчаливый
   * `null` вместо каталога выглядел бы как пустой каталог. Но объявлять дефектом
   * ожидаемые 204 нельзя — кнопка «Пришёл» падала бы после успешного нажатия.
   */
  readonly empty?: boolean;
}

export interface HttpTransport {
  request<T>(options: RequestOptions): Promise<T>;
}

export function createHttpTransport(options: HttpTransportOptions): HttpTransport {
  const doFetch = options.fetchImpl ?? globalThis.fetch;

  return {
    async request<T>(request: RequestOptions): Promise<T> {
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (request.auth) Object.assign(headers, authHeader(options.getInitData()));
      if (request.body !== undefined) headers['Content-Type'] = 'application/json';

      const url = `${options.baseUrl}${request.path}${buildQuery(request.query)}`;

      let response: Response;
      try {
        response = await doFetch(url, {
          method: request.method,
          headers,
          body: request.body === undefined ? undefined : JSON.stringify(request.body),
        });
      } catch (cause) {
        // Сеть не дошла до сервера: отдельный ключ, потому что «сервер не отвечает»
        // и «сервер отказал» — разные ситуации с разными действиями клиента.
        throw new ApiFailure('err.network', 0, { reason: String(cause) });
      }

      const raw = await response.text();
      const parsed = tryParseJson(raw);

      if (!response.ok) {
        throw parseErrorBody(response.status, parsed.ok ? parsed.value : undefined);
      }
      // Ожидаемый 204: тела нет и не должно быть. Возвращается `undefined`, а вызывающий
      // типизирует такой метод как `Promise<void>` — см. `api/cabinet.ts`.
      if (request.empty) return undefined as T;

      if (!parsed.ok) {
        // 2xx без разбираемого тела там, где мы ждём данные, — сломанный контракт,
        // а не пустой результат. Вернув здесь `null`, мы получили бы пустой каталог
        // вместо сообщения о том, что перед бэкендом стоит чужая страница.
        throw new ApiFailure('err.unparseable', response.status, { status: response.status });
      }
      return parsed.value as T;
    },
  };
}

type ParsedBody = { ok: true; value: unknown } | { ok: false };

function tryParseJson(raw: string): ParsedBody {
  if (raw.length === 0) return { ok: false };
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch {
    return { ok: false };
  }
}
